import { useEffect, useMemo, useState } from "react";
import { Sparkles } from "lucide-react";
import loadingDogPhoto from "@/assets/hero-yorkie.jpg";

// Rotating status messages. Spec-supplied wording.
const MESSAGES = [
  "Analyzing your pup…",
  "Creating your new look…",
  "Adding the finishing touches…",
];

// A premium, frontend-only loading animation for the AI grooming preview.
//   * The dog silhouette is stationary in the center (line-art SVG).
//   * A pair of grooming scissors orbits smoothly around the dog.
//   * The scissors subtly open/close, creating a "snip" motion.
//   * After each snip, small fur particles drift outward and fade.
//   * No emoji, no cartoon flair — quiet premium-studio feel.
//
// This component ONLY changes the visual "please wait" — it does not touch
// the Gemini call, credit accounting, or any Whop code.
export default function GeneratingStep({ styleName }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  // Fur particles emitted by the scissors on each snip. Each has a unique
  // key, a launch angle (matching the scissors' current orbit position),
  // and a lifetime after which it's removed from the DOM.
  const [furs, setFurs] = useState([]);

  // Rotate status messages every ~3.5s.
  useEffect(() => {
    const start = performance.now();
    const iv = setInterval(() => {
      const t = (performance.now() - start) / 1000;
      setElapsed(t);
      setMsgIdx(Math.min(MESSAGES.length - 1, Math.floor(t / 3.5) % MESSAGES.length));
    }, 200);
    return () => clearInterval(iv);
  }, []);

  // Emit ~2 fur particles per snip (scissors close every ~1.5s).
  useEffect(() => {
    let id = 0;
    const iv = setInterval(() => {
      const now = performance.now();
      const orbitAngle = ((now / 1000) * 60) % 360; // matches CSS animation duration
      const count = 2;
      const batch = Array.from({ length: count }, (_, i) => ({
        id: `f_${id++}`,
        angle: orbitAngle + (i - count / 2) * 12,
        drift: 22 + Math.random() * 18,
        size: 4 + Math.random() * 3,
        born: now,
      }));
      setFurs((prev) => [...prev.slice(-10), ...batch]);
      // Clean up old particles after ~1.4s (matches CSS fur-fade duration).
      setTimeout(() => {
        setFurs((prev) => prev.filter((f) => f.id !== batch[0].id && f.id !== batch[1]?.id));
      }, 1400);
    }, 1500);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24" data-testid="generating-step">
      {/* One-off keyframes / CSS for the animation. Kept local so nothing
          else in the app is affected. */}
      <style>{ANIMATION_CSS}</style>

      <div className="text-center fade-up">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#ffe8e0] text-[#c84125] text-xs font-bold tracking-widest uppercase px-3 py-1.5">
          <Sparkles className="w-3.5 h-3.5" /> {styleName ? `Applying ${styleName}` : "Generating"}
        </span>
        <h1 className="font-display mt-4 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
          Creating your grooming preview...
        </h1>
      </div>

      <div className="mt-10 rounded-3xl border border-[#eae4d9] bg-white p-6 sm:p-8 shadow-[0_18px_45px_-12px_rgba(15,23,42,0.18)] fade-up delay-1">
        <div
          data-testid="generating-scene"
          className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-[#fdfbf7] to-[#f3efe6] border border-[#eae4d9]"
        >
          {/* Soft ambient rings behind the scene */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-[62%] aspect-square rounded-full border border-[#eae4d9]/70" />
          </div>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-[44%] aspect-square rounded-full border border-[#eae4d9]/60" />
          </div>

          {/* Stationary line-art dog. Absolutely centered — never rotates. */}
          <div
            data-testid="generating-dog"
            className="absolute inset-0 flex items-center justify-center"
          >
            <DogLineArt />
          </div>

          {/* Orbiting scissors + fur particle spawns. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="gp-orbit-wrap">
              <div className="gp-orbit" data-testid="generating-scissors-orbit">
                <div className="gp-scissors-anchor">
                  <ScissorsSVG />
                </div>
              </div>
            </div>
          </div>

          {/* Fur particles — anchored at the center; each is transformed
              outward on its own random angle then fades. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {furs.map((f) => (
              <FurParticle key={f.id} angle={f.angle} drift={f.drift} size={f.size} />
            ))}
          </div>
        </div>

        <p
          data-testid="generating-message"
          className="mt-6 text-center font-display text-base sm:text-lg font-semibold text-[#0f172a] min-h-[1.75rem] transition-opacity"
        >
          {MESSAGES[msgIdx]}
        </p>
        <p className="mt-3 text-center text-xs text-[#64748b]">
          {elapsed > 12
            ? "Almost there — detailed coats can take a little longer."
            : "This usually takes 5–20 seconds."}
        </p>
      </div>
    </div>
  );
}

// Yorkshire Terrier photo, softly masked into a rounded frame so the scissors
// can orbit around it cleanly. Stationary — never rotates. Sits on a soft
// ground shadow. This is the ONLY place the Yorkie photo is used in the app.
function DogLineArt() {
  return (
    <div className="relative w-[58%] max-w-[240px] aspect-square flex items-center justify-center">
      {/* Soft ground shadow keeps the pup grounded inside the scene. */}
      <div
        className="pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 w-[80%] h-3 rounded-full bg-[#1a1712]/25 blur-md"
        aria-hidden="true"
      />
      {/* Rounded photo. Uses a soft inner ring instead of a hard circle mask
          so the image reads premium rather than sticker-like. */}
      <div className="relative w-full h-full rounded-full overflow-hidden bg-[#f3efe6] shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] ring-1 ring-[#eae4d9]">
        <img
          src={loadingDogPhoto}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          draggable={false}
        />
        {/* Subtle vignette so the scissors have contrast where they cross the
            photo edges. */}
        <div className="pointer-events-none absolute inset-0 rounded-full shadow-[inset_0_0_28px_rgba(15,23,42,0.14)]" />
      </div>
    </div>
  );
}

// Grooming scissors — clean line-art, subtle metallic tone.
function ScissorsSVG() {
  return (
    <svg
      viewBox="-24 -24 48 48"
      className="w-9 h-9 gp-scissors"
      fill="none"
      stroke="#0f172a"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Bottom handle (ring) */}
      <circle cx="-11" cy="8" r="4" />
      {/* Top handle (ring) */}
      <circle cx="-11" cy="-8" r="4" />
      {/* Pivot (screw) */}
      <circle cx="-3" cy="0" r="1.4" fill="#0f172a" stroke="none" />
      {/* Bottom blade — animates on the .gp-blade-bottom class */}
      <g className="gp-blade gp-blade-bottom">
        <path d="M-7 6 L 18 -1" />
        <path d="M-3 0 L 18 -1" />
      </g>
      {/* Top blade — animates on the .gp-blade-top class */}
      <g className="gp-blade gp-blade-top">
        <path d="M-7 -6 L 18 1" />
        <path d="M-3 0 L 18 1" />
      </g>
    </svg>
  );
}

// Single fur particle. Absolutely positioned at the scene centre and
// translated outward along `angle`.
function FurParticle({ angle, drift, size }) {
  const style = useMemo(
    () => ({
      "--angle": `${angle}deg`,
      "--drift": `${drift}px`,
      "--size": `${size}px`,
    }),
    [angle, drift, size]
  );
  return <span className="gp-fur" style={style} aria-hidden="true" />;
}

// Local CSS — kept in the same file so nothing else in the app depends on it.
const ANIMATION_CSS = `
.gp-orbit-wrap {
  position: relative;
  width: 66%;
  aspect-ratio: 1 / 1;
  max-width: 260px;
}
.gp-orbit {
  position: absolute;
  inset: 0;
  animation: gp-orbit-spin 6s linear infinite;
  transform-origin: 50% 50%;
  will-change: transform;
}
@keyframes gp-orbit-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
/* Scissors sit at the top of the orbit circle then get rotated by the parent. */
.gp-scissors-anchor {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Keep the scissors themselves visually upright while the orbit spins.
     A counter-rotation of the same 6s duration cancels the parent rotation
     enough for the tool to always read as "held sideways" rather than
     tumbling. */
  animation: gp-scissors-tilt 6s linear infinite;
  will-change: transform;
  filter: drop-shadow(0 3px 6px rgba(15, 23, 42, 0.2));
}
@keyframes gp-scissors-tilt {
  /* Counter-rotate at the same rate as the orbit so the scissors always
     point tangent to the circle — like they'd be held by a groomer. */
  from { transform: translate(-50%, -50%) rotate(-90deg); }
  to   { transform: translate(-50%, -50%) rotate(-450deg); }
}
/* Snip motion — the two blades hinge around the pivot at (-3,0). */
.gp-blade {
  transform-origin: -3px 0;
  animation: gp-snip 1.5s ease-in-out infinite;
}
.gp-blade-top    { animation-name: gp-snip-top; }
.gp-blade-bottom { animation-name: gp-snip-bottom; }
@keyframes gp-snip-top {
  0%, 100% { transform: rotate(0deg); }
  50%      { transform: rotate(9deg); }
}
@keyframes gp-snip-bottom {
  0%, 100% { transform: rotate(0deg); }
  50%      { transform: rotate(-9deg); }
}
/* Fur particle — starts at the centre, drifts outward on --angle, fades. */
.gp-fur {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size);
  height: var(--size);
  border-radius: 999px;
  background: radial-gradient(circle at 30% 30%, #f8f1e6 0%, #d9cdb5 70%, rgba(217,205,181,0) 100%);
  transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(var(--drift) * -1));
  opacity: 0.9;
  animation: gp-fur-fade 1.4s ease-out forwards;
  will-change: transform, opacity;
  pointer-events: none;
}
@keyframes gp-fur-fade {
  0%   { opacity: 0.9; transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-8px) scale(1); }
  60%  { opacity: 0.55; }
  100% { opacity: 0;   transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(var(--drift) * -1)) scale(0.8); }
}
@media (prefers-reduced-motion: reduce) {
  .gp-orbit, .gp-scissors-anchor, .gp-blade, .gp-fur { animation: none !important; }
}
`;
